'use client'

import {useSearchParams} from 'next/navigation'
import {type OmitStrict} from 'type-zoo'
import {grid} from '~/design-system/foundations'
import {
  HgArticleListing,
  type AspectRatio,
  type HgArticleListingIndexComponentProps,
} from '~/design-system/hg/components'
import HgPaginationBlock from '~/design-system/hg/modules/HgPaginationBlock'

export type ArticleData = OmitStrict<
  HgArticleListingIndexComponentProps,
  'variant' | 'aspectRatio'
>

export type HgArticleListingIndexProps = {
  articles: ArticleData[]
} & {
  aspectRatio: Extract<AspectRatio, '16:9'>
  showListingSubheading: boolean
}

const PAGINATION_ITEM_THRESHOLD = 8
const ITEMS_PER_PAGE = 8

const HgArticleListingIndex = ({
  articles,
  aspectRatio,
  showListingSubheading,
}: HgArticleListingIndexProps) => {
  const searchParams = useSearchParams()
  const page = Number(searchParams.get('page')) || 1

  const articlesToDisplay = articles.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  )

  const showSubheading =
    !!showListingSubheading && articles.every(article => !!article.subheading)

  const shouldShowPagination = articles.length > PAGINATION_ITEM_THRESHOLD

  return (
    <div className="w-full pt-s8">
      <div
        className={grid({
          className: 'mx-auto scroll-mt-blog-navbars-height gap-y-s4',
        })}
        id="listing-top"
      >
        {articlesToDisplay.map((article, idx) => (
          <HgArticleListing
            key={idx}
            aspectRatio={aspectRatio}
            variant="index"
            showSubheading={showSubheading}
            {...article}
          />
        ))}
      </div>

      {shouldShowPagination && (
        <HgPaginationBlock
          data={articles}
          controlProps={{itemsPerPage: ITEMS_PER_PAGE}}
          scrollToId="listing-top"
        />
      )}
    </div>
  )
}

export default HgArticleListingIndex
